import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
  Lang: i18n.language,
});

const appendParams = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === "" || value === null || value === undefined) return;
    query.append(key, String(value));
  });
  return query.toString();
};

const extractListPayload = (responseData) => {
  const root = responseData?.data ?? responseData;
  const rows = Array.isArray(root?.data)
    ? root.data
    : Array.isArray(root)
      ? root
      : [];
  const pagination = root?.pagination || responseData?.pagination || null;
  const summary = root?.summary || null;
  return { rows, pagination, summary };
};

export const fetchTargetCustomers = createAsyncThunk(
  "targetCustomers/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const qs = appendParams({ per_page: 20, page: 1, ...params });
      const res = await axios.get(
        `${BASE_URL}/target-customers${qs ? `?${qs}` : ""}`,
        { headers: getHeaders() }
      );
      const { rows, pagination, summary } = extractListPayload(res.data);
      return { data: rows, pagination, summary };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch target customers"
      );
    }
  }
);

export const fetchTargetCustomerById = createAsyncThunk(
  "targetCustomers/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/target-customers/${id}`, {
        headers: getHeaders(),
      });
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch target customer"
      );
    }
  }
);

export const fetchTargetCustomerCities = createAsyncThunk(
  "targetCustomers/fetchCities",
  async ({ search = "", page = 1, per_page = 20 } = {}, { rejectWithValue }) => {
    try {
      const qs = appendParams({ search, page, per_page });
      const res = await axios.get(
        `${BASE_URL}/target-customers/cities${qs ? `?${qs}` : ""}`,
        { headers: getHeaders() }
      );
      const { rows, pagination } = extractListPayload(res.data);
      return { data: rows, pagination };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch cities"
      );
    }
  }
);

export const fetchTargetCustomerCategories = createAsyncThunk(
  "targetCustomers/fetchCategories",
  async ({ search = "", page = 1, per_page = 20 } = {}, { rejectWithValue }) => {
    try {
      const qs = appendParams({ search, page, per_page });
      const res = await axios.get(
        `${BASE_URL}/target-customers/categories${qs ? `?${qs}` : ""}`,
        { headers: getHeaders() }
      );
      const { rows, pagination } = extractListPayload(res.data);
      return { data: rows, pagination };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch categories"
      );
    }
  }
);

const initialState = {
  list: { data: [], pagination: null, summary: null },
  record: null,
  isLoading: false,
  detailLoading: false,
  error: null,
  detailError: null,
};

const targetCustomersSlice = createSlice({
  name: "targetCustomers",
  initialState,
  reducers: {
    clearTargetCustomerState: (state) => {
      state.error = null;
      state.detailError = null;
    },
    clearTargetCustomerDetail: (state) => {
      state.record = null;
      state.detailError = null;
      state.detailLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTargetCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTargetCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = {
          data: action.payload.data || [],
          pagination: action.payload.pagination || null,
          summary: action.payload.summary || null,
        };
      })
      .addCase(fetchTargetCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.list = { data: [], pagination: null, summary: null };
      })
      .addCase(fetchTargetCustomerById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchTargetCustomerById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchTargetCustomerById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload;
        state.record = null;
      });
  },
});

export const { clearTargetCustomerState, clearTargetCustomerDetail } =
  targetCustomersSlice.actions;
export default targetCustomersSlice.reducer;
